// ============================================================================
// ChatSearchPanel.tsx — ВСТРОЕННЫЙ поиск по сообщениям (без окон и таблиц).
//
// Один компонент для ДВУХ мест:
//   • лента чата (ChatThread) — «этот чат» по умолчанию, можно переключить на
//     «все чаты» (переход в другой чат отдаёт родителю onOpenMessage);
//   • экран поиска (ChatPage) — «все чаты» по умолчанию.
//
// SSoT данных — существующий RPC MessengerService.SearchMessages. Фильтры по
// дате/отправителю/вложениям применяются на клиенте (серверного фильтра в API
// нет — зафиксировано в lib/chatSearch.ts). Новых RPC/событий не вводим:
// живые правки/удаления сообщений приходят существующими событиями шины.
// ============================================================================

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertCircle, MessageSquare, Paperclip, RotateCw, Search, SlidersHorizontal, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { createLogger } from '@/lib/logger';
import { friendlyError } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import {
  applyChatSearchFilters,
  chatSearchDateLabel,
  chatSearchFilterCount,
  CHAT_SEARCH_LIMIT,
  EMPTY_CHAT_SEARCH_FILTERS,
  highlightSegments,
  isChatSearchQueryReady,
  searchChatMessages,
  type ChatAttachmentsFilter,
  type ChatSearchFilters,
  type ChatSearchHit,
} from '@/lib/chatSearch';

const log = createLogger('ChatSearchPanel');

export interface ChatSearchUser {
  id: string;
  name: string;
}

interface ChatSearchPanelProps {
  users: ChatSearchUser[];
  /** Текущий чат ('' — его нет: ищем по всем) */
  conversationId?: string;
  /** Участники текущего чата (для фильтра по отправителю) */
  memberIds?: string[];
  defaultScope?: 'chat' | 'all';
  allowScopeSwitch?: boolean;
  autoFocus?: boolean;
  /** Занимать всю высоту родителя (экран поиска) вместо компактной панели */
  fill?: boolean;
  /** Сведения о вложениях из загруженной ленты (id → есть ли вложения) */
  attachmentIndex?: Map<string, boolean>;
  /** Открыть результат; query — запрос, по которому он найден (для подсветки) */
  onOpen: (hit: ChatSearchHit, query: string) => void;
  onClose?: () => void;
}

type Scope = 'chat' | 'all';

export function ChatSearchHighlight({ text, query }: { text: string; query: string }) {
  const parts = useMemo(() => highlightSegments(text, query), [text, query]);
  return (
    <>
      {parts.map((p, i) => (p.match
        ? <mark key={i} className="rounded-sm bg-amber-300 px-0.5 text-amber-950">{p.text}</mark>
        : <span key={i}>{p.text}</span>))}
    </>
  );
}

export function ChatSearchPanel({
  users, conversationId = '', memberIds, defaultScope = 'chat', allowScopeSwitch = true,
  autoFocus = false, fill = false, attachmentIndex, onOpen, onClose,
}: ChatSearchPanelProps) {
  const canSearchChat = !!conversationId;
  const [scope, setScope] = useState<Scope>(canSearchChat ? defaultScope : 'all');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<ChatSearchFilters>(EMPTY_CHAT_SEARCH_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hits, setHits] = useState<ChatSearchHit[]>([]);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const effectiveConversationId = scope === 'chat' && canSearchChat ? conversationId : '';
  const ready = isChatSearchQueryReady(query);

  // Поиск на сервере (дебаунс 300 мс; отмена устаревших ответов флагом alive).
  // «Ищем…» включается СРАЗУ при готовом запросе — иначе первые 300 мс
  // показывалось «Ничего не найдено» (до отправки запроса).
  useEffect(() => {
    let alive = true;
    if (!ready) {
      setHits([]);
      setSearchedQuery('');
      setLoading(false);
      setError(null);
      return () => { alive = false; };
    }
    setLoading(true);
    setError(null);
    const q = query.trim();
    const timer = setTimeout(() => {
      if (!alive) return;
      searchChatMessages(q, effectiveConversationId)
        .then((items) => {
          if (!alive) return;
          setHits(items);
          setSearchedQuery(q);
        })
        .catch((e: unknown) => {
          if (!alive) return;
          log.error('Поиск по сообщениям не выполнен', e);
          setError(friendlyError(e));
          setHits([]);
        })
        .finally(() => {
          if (alive) setLoading(false);
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query, effectiveConversationId, reloadKey, ready]);

  // ЖИВЫЕ ОБНОВЛЕНИЯ (существующие события шины): текст найденных сообщений
  // мог измениться, сообщение удалено или пришло новое совпадение —
  // перезапрашиваем результаты (дебаунс). У правки есть точное событие
  // chat.message.edited.{conv}; удаление публикует chat.conversation.updated
  // (точечного deleted-события в API нет). Подписка активна и при пустой
  // выдаче: новое сообщение может стать первым совпадением.
  useEffect(() => {
    if (!ready) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const sameChat = (topic: string, prefix: string) => {
      if (!effectiveConversationId) return true;
      return topic.startsWith(prefix) && topic.slice(prefix.length) === effectiveConversationId;
    };
    const unsub = eventBusHub.subscribe((ev) => {
      const isCreated = sameChat(ev.topic, 'chat.message.created.');
      const isEdited = sameChat(ev.topic, 'chat.message.edited.');
      const isConvUpdate = ev.topic === 'chat.conversation.updated'
        || (!!effectiveConversationId && ev.topic === `chat.conversation.updated.${effectiveConversationId}`);
      if (!isCreated && !isEdited && !isConvUpdate) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setReloadKey((v) => v + 1), 500);
    });
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, [effectiveConversationId, ready]);

  const senderOptions = useMemo(() => {
    const pool = scope === 'chat' && memberIds && memberIds.length > 0
      ? users.filter((u) => memberIds.includes(u.id))
      : users;
    const byId = new Map<string, string>();
    for (const u of pool) {
      if (u.id) byId.set(u.id, u.name || u.id.slice(0, 8));
    }
    // Авторы из выдачи (сервер отдаёт senderName): фильтр доступен, даже если
    // список контактов не загружен/ограничен правами.
    for (const h of hits) {
      if (h.senderId && !byId.has(h.senderId)) {
        byId.set(h.senderId, h.senderName || h.senderId.slice(0, 8));
      }
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ru'));
  }, [users, memberIds, scope, hits]);

  const senderLabel = (hit: ChatSearchHit) => {
    if (hit.senderName) return hit.senderName;
    if (hit.senderId) return users.find((u) => u.id === hit.senderId)?.name || hit.senderId.slice(0, 8);
    return 'Неизвестный';
  };

  const filtered = useMemo(
    () => applyChatSearchFilters(hits, filters, attachmentIndex),
    [hits, filters, attachmentIndex],
  );
  const activeFilters = chatSearchFilterCount(filters);
  // Вложения: сервер отдаёт их в результатах (SSoT) — фильтр доступен и в
  // «всех чатах»; лента (attachmentIndex) остаётся резервом. Пока выдача пуста,
  // отбор разрешён (пользователь задаёт его до/после запроса). Если сведения
  // есть хотя бы у части выдачи — фильтр работает, а результаты без сведений
  // честно скрываются и считаются отдельно.
  const attachmentsAvailable = !!attachmentIndex
    || hits.length === 0
    || hits.some((h) => h.hasAttachments !== null);
  const showChatTitle = scope === 'all' || !conversationId;

  const resetFilters = () => {
    setFilters(EMPTY_CHAT_SEARCH_FILTERS);
  };

  const switchScope = (next: Scope) => {
    if (next === scope) return;
    // Старые результаты другой области не показываем под новым заголовком:
    // эффект сразу запросит новую выдачу (query остаётся). Отправитель
    // сбрасывается — в другой области его может не быть в списке участников,
    // и прежний отбор дал бы пустую выдачу без объяснения.
    setHits([]);
    setSearchedQuery('');
    setFilters((f) => ({ ...f, senderId: '' }));
    setScope(next);
  };

  return (
    <div className={`${fill ? 'flex flex-1 min-h-0' : 'shrink-0'} flex-col border-b bg-card/70`}>
      {/* Поле поиска */}
      <div className="flex items-center gap-2 px-2 py-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            autoFocus={autoFocus}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={scope === 'chat' ? 'Поиск в этом чате…' : 'Поиск по всем чатам…'}
            className="h-8 pl-7 pr-7 text-[12px]"
            aria-label="Поиск по сообщениям"
          />
          {query !== '' && (
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted/60"
              onClick={() => setQuery('')}
              aria-label="Очистить запрос"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {onClose && (
          <button type="button" className="p-1.5 rounded-full hover:bg-muted/40" onClick={onClose} aria-label="Закрыть поиск" title="Закрыть поиск">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Область поиска: текущий чат / все чаты */}
      {allowScopeSwitch && canSearchChat && (
        <div className="flex items-center gap-1 px-2 pb-1.5" role="group" aria-label="Где искать">
          <ScopeChip active={scope === 'chat'} onClick={() => switchScope('chat')}>Этот чат</ScopeChip>
          <ScopeChip active={scope === 'all'} onClick={() => switchScope('all')}>Все чаты</ScopeChip>
        </div>
      )}

      {/* Фильтры */}
      <div className="flex items-center gap-2 px-2 pb-1.5">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] transition-colors ${filtersOpen || activeFilters ? 'border-primary/50 text-primary bg-primary/5' : 'text-muted-foreground hover:bg-muted/40'}`}
          aria-expanded={filtersOpen}
        >
          <SlidersHorizontal className="h-3 w-3" />
          Фильтры{activeFilters ? ` · ${activeFilters}` : ''}
        </button>
        {activeFilters > 0 && (
          <button type="button" className="text-[10px] text-muted-foreground underline underline-offset-2 hover:text-foreground" onClick={resetFilters}>
            сбросить
          </button>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground" aria-live="polite">
          {loading ? 'Ищем…' : ready && !error ? `${filtered.items.length}` : ''}
        </span>
      </div>

      {filtersOpen && (
        <div className="grid grid-cols-2 gap-1.5 px-2 pb-2 sm:grid-cols-4">
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] uppercase tracking-wide text-muted-foreground">С даты</span>
            <input
              type="date"
              value={filters.from}
              max={filters.to || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="h-7 w-full rounded border bg-background px-1.5 text-[11px]"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] uppercase tracking-wide text-muted-foreground">По дату</span>
            <input
              type="date"
              value={filters.to}
              min={filters.from || undefined}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="h-7 w-full rounded border bg-background px-1.5 text-[11px]"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] uppercase tracking-wide text-muted-foreground">Отправитель</span>
            <select
              value={filters.senderId}
              onChange={(e) => setFilters((f) => ({ ...f, senderId: e.target.value }))}
              className="h-7 w-full rounded border bg-background px-1 text-[11px]"
            >
              <option value="">Любой</option>
              {senderOptions.map((u) => <option key={u.id} value={u.id}>{u.name || u.id}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-0.5" title={attachmentsAvailable ? 'Сведения о вложениях приходят с сервера; для старых сообщений — из загруженной ленты' : 'Для найденных сообщений нет сведений о вложениях'}>
            <span className="text-[9.5px] uppercase tracking-wide text-muted-foreground">Вложения</span>
            <select
              value={filters.attachments}
              disabled={!attachmentsAvailable}
              onChange={(e) => setFilters((f) => ({ ...f, attachments: e.target.value as ChatAttachmentsFilter }))}
              className="h-7 w-full rounded border bg-background px-1 text-[11px] disabled:opacity-50"
            >
              <option value="any">Любые</option>
              <option value="with">С вложениями</option>
              <option value="without">Без вложений</option>
            </select>
          </label>
          {!attachmentsAvailable && (
            <p className="col-span-2 text-[9.5px] leading-snug text-muted-foreground sm:col-span-4">
              Отбор по вложениям недоступен: для выдачи нет сведений о вложениях (сервер прислал их без файлов).
            </p>
          )}
        </div>
      )}

      {/* Результаты / состояния */}
      <div className={`${fill ? 'flex-1 min-h-0' : 'max-h-[min(45vh,360px)]'} overflow-y-auto overscroll-contain`}>
        {!ready && (
          <Hint>
            Введите не менее 2 символов — поиск идёт по вашим чатам
            {scope === 'chat' ? ' (текущему)' : ''}.
          </Hint>
        )}

        {ready && loading && filtered.items.length === 0 && <Hint pulse>Ищем совпадения…</Hint>}

        {ready && !loading && error && (
          <div className="p-3 text-[11px]">
            <div className="flex items-start gap-2 text-destructive">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span className="min-w-0 break-words">{error}</span>
            </div>
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10.5px] hover:bg-muted/40"
              onClick={() => setReloadKey((v) => v + 1)}
            >
              <RotateCw className="h-3 w-3" /> Повторить
            </button>
          </div>
        )}

        {ready && !loading && !error && filtered.items.length === 0 && (
          <div className="p-3 text-[11px] text-muted-foreground">
            {hits.length > 0 && activeFilters > 0 ? (
              <div className="flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                <span>
                  Найдено {hits.length} совпадений{searchedQuery ? ` по запросу «${searchedQuery}»` : ''},
                  но ни одно не подходит под фильтры.
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                <span>Ничего не найдено{searchedQuery ? ` по запросу «${searchedQuery}»` : ''}.</span>
              </div>
            )}
            {filtered.hiddenUnknownAttachments > 0 && (
              <p className="mt-1 text-[9.5px] leading-snug">
                Скрыто результатов без сведений о вложениях: {filtered.hiddenUnknownAttachments}.
              </p>
            )}
            {activeFilters > 0 && (
              <button type="button" className="mt-1.5 underline underline-offset-2 hover:text-foreground" onClick={resetFilters}>
                Сбросить фильтры
              </button>
            )}
          </div>
        )}

        {ready && !error && filtered.items.length > 0 && (
          <div>
            {filtered.items.map((hit) => (
              <button
                key={hit.id}
                type="button"
                onClick={() => onOpen(hit, searchedQuery)}
                className="w-full border-t border-border/40 px-3 py-2 text-left transition-colors hover:bg-accent/40 focus:bg-accent/40 focus:outline-none"
              >
                <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  {showChatTitle && (
                    <span className="truncate font-medium text-foreground/80">{hit.conversationTitle || 'Чат'}</span>
                  )}
                  <span className="truncate">{senderLabel(hit)}</span>
                  <span className="ml-auto shrink-0">{chatSearchDateLabel(hit.createdAt)}</span>
                </div>
                <div className="mt-0.5 line-clamp-3 text-[12px] leading-snug [overflow-wrap:anywhere]">
                  <ChatSearchHighlight text={hit.snippet || hit.text} query={searchedQuery} />
                </div>
                {hit.hasAttachments === true && (
                  <span className="mt-0.5 inline-flex items-center gap-1 text-[9.5px] text-muted-foreground">
                    <Paperclip className="h-2.5 w-2.5" /> вложение
                  </span>
                )}
              </button>
            ))}
            {filtered.hiddenUnknownAttachments > 0 && (
              <p className="px-3 py-2 text-[9.5px] leading-snug text-muted-foreground">
                Скрыто результатов без сведений о вложениях: {filtered.hiddenUnknownAttachments} (поиск не отдаёт вложения для старых сообщений).
              </p>
            )}
            {hits.length >= CHAT_SEARCH_LIMIT && (
              <p className="px-3 py-2 text-[9.5px] text-muted-foreground">Показаны первые {CHAT_SEARCH_LIMIT} совпадений — уточните запрос.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Hint({ children, pulse = false }: { children: ReactNode; pulse?: boolean }) {
  return (
    <div className={`px-3 py-3 text-[11px] leading-snug text-muted-foreground ${pulse ? 'animate-pulse' : ''}`}>
      {children}
    </div>
  );
}

function ScopeChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-0.5 text-[10.5px] transition-colors ${active ? 'border-primary/50 bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted/40'}`}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}
